import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import type { AssetStatusChange, Policy, PolicyDriver } from "@shared/schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StateSelect } from "@/components/state-select";
import { useToast } from "@/hooks/use-toast";
import { useUpdatePolicyDrivers } from "@/lib/api";
import { toIsoDate, fromIsoDate } from "@/lib/formatters";
import { PolicyMultiSelect } from "./PolicyMultiSelect";
import { AssetStatusSelect, type AssetLifecycleStatus } from "./AssetStatusSelect";
import {
  defaultPolicySelection,
  keyForDriverDisplay,
  planAssetLinkWrites,
  policiesLinkedToAsset,
} from "./asset-helpers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policies: Policy[];
  editData: PolicyDriver | null;
  editPolicyId: string | null;
}

interface FormErrors {
  name?: string;
  policies?: string;
}

export function DriverDialog({ open, onOpenChange, policies, editData, editPolicyId }: Props) {
  const { toast } = useToast();
  const updateDrivers = useUpdatePolicyDrivers();

  const isEdit = !!editData;
  const [name, setName] = useState("");
  const [licenseNumber, setLicenseNumber] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateOfHire, setDateOfHire] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [status, setStatus] = useState<AssetLifecycleStatus>("active");
  const [selectedPolicyIds, setSelectedPolicyIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  // CSR-2335 — the "Related Policies" picker offers every policy on the insured,
  // whatever its status, so it can be linked to (or unlinked from) any term.
  const showPicker = policies.length > 0;

  useEffect(() => {
    if (!open) return;
    if (editData) {
      setName(editData.name ?? `${editData.firstName ?? ""} ${editData.lastName ?? ""}`.trim());
      setLicenseNumber(editData.licenseNumber ?? "");
      setLicenseState(editData.licenseState ?? "");
      setDateOfBirth(editData.dateOfBirth ?? "");
      setDateOfHire(editData.dateOfHire ?? "");
      setYearsExperience(editData.yearsExperience != null ? String(editData.yearsExperience) : "");
      setStatus(editData.removedAt ? "removed" : "active");
      const editKey = keyForDriverDisplay(editData);
      const linked = editKey
        ? policiesLinkedToAsset<PolicyDriver>(policies, "drivers", keyForDriverDisplay, editKey)
        : [];
      setSelectedPolicyIds(linked.length > 0 ? linked : editPolicyId ? [editPolicyId] : []);
    } else {
      setName("");
      setLicenseNumber("");
      setLicenseState("");
      setDateOfBirth("");
      setDateOfHire("");
      setYearsExperience("");
      setStatus("active");
      setSelectedPolicyIds(defaultPolicySelection(policies));
    }
    setErrors({});
    // `policies.length` (not `policies`) so a TanStack Query background refetch
    // — new array identity, same length — doesn't reset in-progress form edits.
  }, [open, editData, editPolicyId, policies.length]);

  function handleSubmit() {
    const next: FormErrors = {};
    if (!name.trim()) next.name = "Name is required.";
    if (selectedPolicyIds.length === 0) next.policies = "Select at least one policy.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const nowIso = new Date().toISOString();
    const normalizedName = name.trim();
    const parts = normalizedName.split(/\s+/);
    // Match existing entries by the driver's ORIGINAL identity so a rename
    // still finds the rows to update/unlink.
    const matchKey =
      (isEdit && editData ? keyForDriverDisplay(editData) : null) ??
      keyForDriverDisplay({ name: normalizedName }) ??
      `nm:${normalizedName.toLowerCase()}`;

    const buildEntry = (existing: PolicyDriver | null): PolicyDriver => ({
      // Preserve carrier/DC-owned fields not in the form (databaseId, birthday,
      // …). When appending to a newly linked policy, seed from the edited entry
      // so the cross-policy identity (databaseId) stays consistent.
      ...(existing ?? editData ?? {}),
      name: normalizedName,
      firstName: parts.slice(0, -1).join(" ") || undefined,
      lastName: parts.length > 1 ? parts[parts.length - 1] : undefined,
      licenseNumber: licenseNumber.trim() || undefined,
      licenseState: licenseState.trim().toUpperCase() || undefined,
      dateOfBirth: dateOfBirth || undefined,
      dateOfHire: dateOfHire || undefined,
      yearsExperience: yearsExperience.trim() ? Number(yearsExperience.trim()) : undefined,
      addedAt: existing?.addedAt ?? nowIso,
      removedAt: null,
    });

    const writes = planAssetLinkWrites<PolicyDriver>({
      // An Add only writes the ticked terms: an unticked term was never looked
      // at, so it must not be read as "unlink" (CSR-2335). Edit pre-checks every
      // term holding the asset, so there unticking is a deliberate unlink.
      policies: isEdit ? policies : policies.filter((p) => selectedPolicyIds.includes(p.id)),
      arrayKey: "drivers",
      identityOf: keyForDriverDisplay,
      matchKey,
      buildEntry,
      selectedPolicyIds,
    });

    // CSR-1404 — an explicit Status change carries a directive the server applies
    // after the lifecycle-preserving merge. Identity targets the saved driver
    // (databaseId, else the normalized name) across its selected policies.
    const statusChanged = isEdit && (status === "removed") !== !!editData?.removedAt;
    const assetStatusChange: AssetStatusChange | undefined = statusChanged
      ? {
          arrayKey: "drivers",
          match: {
            databaseId: editData?.databaseId,
            name: normalizedName || undefined,
            addedAt: editData?.addedAt,
          },
          removed: status === "removed",
        }
      : undefined;
    // Send the directive on a single write; the server propagates the status to
    // every policy the driver is on (the badge is a cross-policy aggregate).
    const selected = new Set(selectedPolicyIds);
    const directiveTargetId = writes.find((w) => selected.has(w.policyId))?.policyId;

    Promise.all(
      writes.map((w) =>
        updateDrivers.mutateAsync({
          id: w.policyId,
          drivers: w.nextArray,
          assetStatusChange:
            assetStatusChange && w.policyId === directiveTargetId ? assetStatusChange : undefined,
        }),
      ),
    )
      .then(() => {
        const linkedCount = selectedPolicyIds.length;
        toast({
          title: isEdit ? "Updated" : "Added",
          description: isEdit
            ? undefined
            : `Linked to ${linkedCount} ${linkedCount === 1 ? "policy" : "policies"}. Note: this list syncs from the carrier; your changes may be replaced on next sync.`,
        });
        onOpenChange(false);
      })
      .catch((err) => {
        toast({
          title: "Failed",
          description: err instanceof Error ? err.message : "Could not save driver.",
          variant: "destructive",
        });
      });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="driver-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Driver" : "Add Driver"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Edits apply immediately. Carrier scrape may overwrite on next sync."
              : "Added drivers may be replaced when the carrier next scrapes the policy."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {showPicker && (
            <PolicyMultiSelect
              policies={policies}
              selected={selectedPolicyIds}
              onChange={setSelectedPolicyIds}
              testId="driver-dialog-policy-picker"
              checkboxTestId={(id) => `driver-policy-checkbox-${id}`}
              error={errors.policies}
            />
          )}

          <div>
            <Label htmlFor="driver-name">Name *</Label>
            <Input
              id="driver-name"
              data-testid="driver-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={errors.name ? "border-destructive" : ""}
            />
            {errors.name && (
              <p data-testid="driver-name-error" className="text-xs text-destructive mt-1">
                {errors.name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="driver-license-number">License #</Label>
              <Input
                id="driver-license-number"
                data-testid="driver-license-number-input"
                value={licenseNumber}
                onChange={(e) => setLicenseNumber(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="driver-license-state">License State</Label>
              <StateSelect
                id="driver-license-state"
                value={licenseState}
                onChange={setLicenseState}
              />
            </div>
            <div>
              <Label htmlFor="driver-date-of-birth">Date of Birth</Label>
              <Input
                id="driver-date-of-birth"
                data-testid="driver-dob-input"
                type="date"
                value={toIsoDate(dateOfBirth)}
                onChange={(e) => setDateOfBirth(fromIsoDate(e.target.value))}
              />
            </div>
            <div>
              <Label htmlFor="driver-date-of-hire">Date of Hire</Label>
              <Input
                id="driver-date-of-hire"
                data-testid="driver-doh-input"
                type="date"
                value={toIsoDate(dateOfHire)}
                onChange={(e) => setDateOfHire(fromIsoDate(e.target.value))}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="driver-years-experience">Years of Experience</Label>
              <Input
                id="driver-years-experience"
                data-testid="driver-years-input"
                value={yearsExperience}
                onChange={(e) => setYearsExperience(e.target.value)}
                inputMode="numeric"
              />
            </div>
          </div>

          {isEdit && (
            <div>
              <Label htmlFor="driver-status">Status</Label>
              <AssetStatusSelect value={status} onChange={setStatus} testId="driver-status-select" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateDrivers.isPending}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={updateDrivers.isPending}
            data-testid="driver-dialog-submit"
            className="cursor-pointer"
          >
            {updateDrivers.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
